import type { HallView } from "@highmarch/app";
import { fmtCompact } from "../kit";
import { fmtClear } from "../modes/challengeWords";
import { CARD_H, CARD_W, GAME_NAME } from "../shareCard";
import { TitlePlate, drawTitlePlate, wornTitlePlate } from "../titleFrameCanvas";

/**
 * The share card's records variant (MP-27, beside the end card of ui/shareCard.ts, ENGAGEMENT B8): the Hall of
 * Records' bests as lines on the same night-blue card, handed to the same share sheet (shareCard.ts `shareCard`) or
 * copied. Nothing leaves the device unless the player sends it.
 */
export interface RecordsCardModel {
  readonly lines: readonly { readonly label: string; readonly value: string }[];
  readonly medals: Readonly<Record<"gold" | "silver" | "bronze", number>>;
}

/** Medal colours on the card (gold, silver, bronze), near the painted medals'. */
const MEDAL_COLOUR = { gold: "#ffd75c", silver: "#d6dde8", bronze: "#d0884a" } as const;
/** Lines the card has room for. */
const MAX_LINES = 7;

/** What the card says: the bests the player has, in the Hall's order (an empty best is left out). */
export function recordsCardModel(v: HallView): RecordsCardModel {
  const dailyBest = v.daily.reduce((b, e) => Math.max(b, e.score), 0), codesWon = v.challenges.filter((c) => c.won).length;
  const lines = [
    v.endless.best.waves > 0 ? { label: "Endless best", value: `Wave ${v.endless.best.waves}` } : null,
    v.iron.stars > 0 ? { label: "Iron stars", value: String(v.iron.stars) } : null,
    v.stars.have > 0 ? { label: "Campaign stars", value: `${v.stars.have} / ${v.stars.max}` } : null,
    v.fastest ? { label: `Fastest clear · level ${v.fastest.level}`, value: fmtClear(v.fastest.ms) } : null,
    dailyBest > 0 ? { label: "Daily Road best", value: fmtCompact(dailyBest) } : null,
    v.races[0] ? { label: `Race Week ${v.races[0].week.slice(-3)}`, value: fmtClear(v.races[0].ms) } : null,
    codesWon > 0 ? { label: "Challenge codes beaten", value: String(codesWon) } : null,
  ].filter((l): l is { label: string; value: string } => l !== null).slice(0, MAX_LINES);
  return { lines, medals: v.medals };
}

/** The text shared with (or instead of) the picture: one line a chat app shows in full. */
export function recordsShareText(m: RecordsCardModel): string {
  const medals = m.medals.gold + m.medals.silver + m.medals.bronze > 0 ? [`Race medals ${m.medals.gold}/${m.medals.silver}/${m.medals.bronze}`] : [];
  return `My ${GAME_NAME} records: ${[...m.lines.map((l) => `${l.label} ${l.value}`), ...medals].join(" · ")}`;
}

/** Draws the card onto a 2D context of CARD_W × CARD_H (the end card's size and palette); `plate`: the worn title frame (MP-23). */
export function drawRecordsCard(g: CanvasRenderingContext2D, m: RecordsCardModel, plate: TitlePlate | null = null): void {
  const bg = g.createLinearGradient(0, 0, 0, CARD_H);
  bg.addColorStop(0, "#1d2740"); bg.addColorStop(1, "#0f1524");
  g.fillStyle = bg; g.fillRect(0, 0, CARD_W, CARD_H);
  g.textAlign = "center"; g.fillStyle = "#ffd75c"; g.font = "bold 40px 'Fredoka Display', sans-serif";
  g.fillText("HALL OF RECORDS", CARD_W / 2, 70);
  g.fillStyle = "#b9c3d8"; g.font = "bold 18px 'Fredoka Display', sans-serif";
  g.fillText("Personal bests", CARD_W / 2, 100);
  // the medals: three discs with their counts
  const medals = ["gold", "silver", "bronze"] as const;
  medals.forEach((k, i) => {
    const x = CARD_W / 2 + (i - 1) * 120, y = 160;
    g.fillStyle = MEDAL_COLOUR[k]; g.beginPath(); g.arc(x - 22, y, 20, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#fff8e6"; g.font = "bold 30px 'Fredoka Display', sans-serif"; g.textAlign = "left";
    g.fillText(String(m.medals[k]), x + 6, y + 11);
  });
  // the lines: label left, value right, on alternating bands
  let y = 230;
  for (const [i, l] of m.lines.entries()) {
    if (i % 2 === 0) { g.fillStyle = "rgba(255, 255, 255, 0.05)"; g.fillRect(40, y - 34, CARD_W - 80, 56); }
    g.textAlign = "left"; g.fillStyle = "#fff8e6"; g.font = "bold 22px 'Fredoka Display', sans-serif";
    g.fillText(l.label, 60, y);
    g.textAlign = "right"; g.fillStyle = "#ffd75c"; g.font = "bold 28px 'Fredoka Display', sans-serif";
    g.fillText(l.value, CARD_W - 60, y + 2);
    y += 60;
  }
  if (plate) drawTitlePlate(g, plate, CARD_W / 2, Math.max(y - 10, CARD_H - 112));    // whose records: the framed name plate
  g.textAlign = "center"; g.fillStyle = "#b98a37"; g.font = "16px 'Fredoka Display', sans-serif";
  g.fillText(GAME_NAME, CARD_W / 2, CARD_H - 18);
}

/** The card as a PNG (null where a canvas cannot be made or encoded). */
export async function renderRecordsCard(m: RecordsCardModel): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W; canvas.height = CARD_H;
  const g = canvas.getContext("2d");
  if (!g) return null;
  drawRecordsCard(g, m, await wornTitlePlate());
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}
